import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { HostingPleskAutomationControls } from './HostingPleskAutomationControls';
import { HostingPleskInventoryCard } from './HostingPleskInventoryCard';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type {
  HostingServerView,
  HostingPleskAccessView,
  HostingPleskProbeView,
} from '@tosho/contracts';
import { hostingPleskApi, HostingPleskApiError } from './hosting-plesk-api';

/** Accès sensible séparé des déclarations. Pas de secret en état React, local
 * storage, historique ou URL. Le champ est vidé avant toute attente réseau.
 * Les chaînes JS/fetch ne sont pas effaçables : ne pas prétendre le contraire.
 */
export function HostingPleskAccessDialog({
  server,
  csrfToken,
  onClose,
  onReauthenticate,
}: {
  readonly server: HostingServerView;
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onReauthenticate?: (() => void) | undefined;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    keyField = useRef<HTMLInputElement>(null),
    alive = useRef(true),
    title = useId();
  const [view, setView] = useState<HostingPleskAccessView | null>(null),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [locked, setLocked] = useState(false),
    [reauth, setReauth] = useState(false),
    [error, setError] = useState<string | null>(null),
    [feedback, setFeedback] = useState<string | null>(null),
    [probe, setProbe] = useState<HostingPleskProbeView | null>(null);
  const [visit, setVisit] = useState(0);
  useEffect(() => {
    alive.current = true;
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    return () => {
      alive.current = false;
      if (keyField.current) keyField.current.value = '';
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  function fail(failure: unknown): void {
    if (!alive.current) return;
    const code = failure instanceof HostingPleskApiError ? failure.code : '';
    if (code === 'RECENT_AUTHENTICATION_REQUIRED') {
      setReauth(true);
      setLocked(true);
      setError('Reconnectez-vous à Tosho pour gérer cet accès sensible.');
    } else if (code === 'AUTHENTICATION_UNAVAILABLE') {
      setError(
        'La vérification de votre session est temporairement indisponible. Réessayez dans un instant.',
      );
    } else if (code === 'PLESK_ACCESS_CONFLICT') {
      setLocked(true);
      setError('Cet accès a changé. Fermez et rouvrez cette fenêtre avant de recommencer.');
    } else if (code === 'PLESK_ACCESS_INVALID')
      setError('Vérifiez l’adresse HTTPS du panneau (port 443 ou 8443) et la clé API.');
    else if (code === 'PLESK_RATE_LIMITED')
      setError('Trop de tentatives. Patientez une minute avant de réessayer.');
    else if (code === 'PLESK_NOT_CONFIGURED') {
      setLocked(true);
      setError('Le stockage protégé doit être configuré par l’exploitant Tosho.');
    } else
      setError(
        'L’accès ou le test Plesk est indisponible. Vérifiez la clé, le certificat HTTPS et les restrictions d’accès dans Plesk.',
      );
  }
  useEffect(() => {
    let active = true;
    void hostingPleskApi
      .read(server.id)
      .then(
        (result) => {
          if (active) setView(result);
        },
        (failure: unknown) => {
          if (active) fail(failure);
        },
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [server.id, visit]);
  const [inventoryBusy, setInventoryBusy] = useState(false);
  const disabled = busy || inventoryBusy || loading || locked || !view?.configured;
  async function run(action: 'save' | 'revoke' | 'probe', form?: HTMLFormElement): Promise<void> {
    if (disabled || !view) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    setProbe(null);
    try {
      if (action === 'save' && form) {
        const origin = (form.elements.namedItem('origin') as HTMLInputElement).value;
        const key = keyField.current?.value ?? '';
        if (keyField.current) keyField.current.value = '';
        const result = await hostingPleskApi.replace(
          server.id,
          view.access?.revision ?? null,
          origin,
          key,
          csrfToken,
        );
        if (alive.current) {
          setView(result);
          setFeedback('Accès enregistré. Vous pouvez maintenant tester la lecture Plesk.');
        }
      } else if (action === 'revoke' && view.access) {
        const result = await hostingPleskApi.revoke(server.id, view.access.revision, csrfToken);
        if (alive.current) {
          setView(result);
          setFeedback(
            'Accès révoqué dans Tosho. La clé reste à supprimer dans Plesk si nécessaire.',
          );
        }
      } else if (action === 'probe' && view.access) {
        const result = await hostingPleskApi.probe(server.id, view.access.revision, csrfToken);
        if (alive.current) setProbe(result);
      }
    } catch (failure) {
      fail(failure);
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run('save', event.currentTarget);
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor hosting-plesk"
      aria-labelledby={title}
      aria-busy={busy || inventoryBusy || loading}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy && !inventoryBusy) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          disabled={busy || inventoryBusy}
          onClick={onClose}
        />
      }
    >
      <form onSubmit={submit} autoComplete="off">
        <header className="tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Accès protégé</p>
            <h2 id={title}>Réglages Plesk</h2>
          </div>
        </header>
        <p className="hosting-plesk__server">{server.label}</p>
        <p>
          Enregistrez une clé API créée dans Plesk, puis lancez un test de lecture. Tosho ne modifie
          pas le serveur pendant ce test.
        </p>
        {loading ? (
          <p role="status">Chargement de l’accès…</p>
        ) : view && !view.configured ? (
          <p role="status">Le stockage protégé n’est pas encore configuré sur cet environnement.</p>
        ) : view ? (
          <p role="status">
            {view.access?.status === 'ACTIVE'
              ? 'Clé enregistrée. Test de lecture disponible.'
              : view?.access?.status === 'REVOKED'
                ? 'Accès révoqué dans Tosho.'
                : 'Aucun accès enregistré.'}
          </p>
        ) : null}
        {error ? (
          <p className="hosting-servers__error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        {error && !busy && (
          <button
            type="button"
            onClick={() => {
              setLocked(false);
              setReauth(false);
              setError(null);
              setLoading(true);
              setVisit((n) => n + 1);
            }}
          >
            Vérifier à nouveau l’accès
          </button>
        )}
        {reauth && onReauthenticate ? (
          <button type="button" onClick={onReauthenticate}>
            Me reconnecter à Tosho
          </button>
        ) : null}
        <fieldset disabled={disabled}>
          <label>
            Adresse HTTPS du panneau
            <input
              name="origin"
              type="url"
              required
              maxLength={300}
              placeholder="https://panneau.exemple.fr:8443"
              autoComplete="off"
            />
          </label>
          <label>
            {view?.access ? 'Nouvelle clé API Plesk' : 'Clé API Plesk'}
            <input
              ref={keyField}
              name="apiKey"
              type="password"
              required
              minLength={16}
              maxLength={512}
              autoComplete="new-password"
              spellCheck={false}
            />
          </label>
          <p className="hosting-plesk__help">
            Pour créer une clé depuis Plesk, ouvrez l’extension officielle « Keychain for API Secret
            Keys ». Restreignez-la à l’IP publique sortante du serveur qui héberge Tosho, puis
            copiez la valeur créée ici.
          </p>
          <p className="hosting-plesk__help">
            La clé peut donner des droits étendus dans Plesk. Limitez son utilisation à l’adresse IP
            de Tosho dans Plesk. Elle ne sera jamais réaffichée ici. Pour remplacer un accès,
            ressaisissez ensemble l’adresse et la clé.
          </p>
          <button type="submit" className="hosting-servers__primary">
            {busy ? 'Traitement…' : 'Enregistrer l’accès'}
          </button>
        </fieldset>
        {view?.access?.status === 'ACTIVE' ? (
          <HostingPleskAutomationControls
            serverId={server.id}
            csrfToken={csrfToken}
            disabled={disabled}
            onBusyChange={setInventoryBusy}
            onFailure={fail}
          />
        ) : null}
        {view?.access?.status === 'ACTIVE' ? (
          <div className="hosting-plesk__actions">
            <button type="button" disabled={disabled} onClick={() => void run('probe')}>
              Tester la lecture Plesk
            </button>
            <button type="button" disabled={disabled} onClick={() => void run('revoke')}>
              Révoquer l’accès Tosho
            </button>
          </div>
        ) : null}
        {feedback ? (
          <p role="status" className="tosho-feedback is-success">
            {feedback}
          </p>
        ) : null}
        {probe ? (
          <div className="hosting-plesk__result tosho-feedback is-success" role="status">
            <strong>Lecture Plesk réussie · version {probe.panelVersion}</strong>
            <p>
              Observé le {new Date(probe.observedAt).toLocaleString('fr-FR')}. Résultat ponctuel,
              non conservé après fermeture.
            </p>
            <p>
              Ce test vérifie la version. Utilisez l’inventaire ci-dessous pour lire les domaines.
              Les sauvegardes sont consultables dans les fiches des serveurs et des sites.
            </p>
          </div>
        ) : null}
        {view?.configured ? (
          <HostingPleskInventoryCard
            serverId={server.id}
            access={view.access}
            csrfToken={csrfToken}
            disabled={busy || loading || locked}
            onBusyChange={setInventoryBusy}
            onReauthenticate={onReauthenticate}
          />
        ) : null}
        <footer>
          <button type="button" onClick={onClose} disabled={busy || inventoryBusy}>
            Fermer
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
